import type { Reducer } from 'react'
import { useReducer, useEffect } from 'react'

import { produce } from 'immer'
import { isUndefined, mapValues, pick } from 'lodash'
import QueryString from 'query-string'
import { useLocation } from 'react-router'

import { useLocationHelper } from '@webapp/hooks'
import { type FilterManagerContextValues } from './FilterManagerContext'
import {
  convertFiltersToFilterTags,
  convertFiltersToQueryParams,
  reducer,
} from './reducer'
import { FilterManagerResolver } from './services'
import {
  type FilterManagerFilter,
  type FilterManagerResolveFunction,
  type FilterManagerState,
  type IFilterManagerFilters,
  type ResolveAlias,
  type UseFilterManagerActions,
} from './types'

export interface UseFilterManagerOptions<Filters = any> {
  /**
   * The params option is an array of url param keys that
   * we want to resolve.
   *
   * @example
   * If the url is `/?user=19`, the param key `user` will
   * have the FilterManager pull the value `19` and resolve it using
   * the `user` resolver.
   *
   * One can add a ':' to give a key an alias key.
   *
   * @example `customer:pref_vendor` is a key that interacts as
   * `customer` in the URL params, but uses the `pref_vendor` resolver
   */
  params?: string[]
  /**
   * An object of param resolvers. The key of the resolver should match
   * the keys in the `params` option.
   *
   * A resolver is used to populate a url param value. This is usually needed
   * when a url param value needs to be populated with additional data to
   * properly display the FilterTag's label.
   *
   * @example
   * If we want to resolve the param `/?vendor=1`, we will pass:
   * ```js
   * {
   *   vendor: (paramValue) => VendorAPI.getById(paramValue).then(res => res.data)
   * }
   * ```
   *
   * @see filtermanager.resolver.ts for predefined resolvers
   */
  resolve?: {
    [key in keyof Filters]?: FilterManagerResolveFunction<Filters, Filters[key]>
  }
  formatLabels?: {
    [key in keyof Filters]?: (filter: Filters[key]) => string
  }
  lockedTags?: { [key in keyof Filters]?: boolean }
  /**
   * An option to combine two params into one FilterTag.
   *
   * Combined tag's label and tooltip can be customized using
   * the `formatLabels` and `formatTooltips` option using
   * the `key` defined for the combined tag.
   *
   * @example `{ daterange: ['date_0', 'date_1'] }`
   */
  combineTags?: {
    [key in keyof Filters]?: (keyof Filters)[]
  }
  hideTags?: {
    [key in keyof Filters]?: ((filter: Filters[key]) => boolean) | boolean
  }
  formatTooltips?: {
    [key in keyof Filters]?: (filter: Filters[key]) => string
  }
  /**
   * Sync URL params with the filters managed in this component.
   *
   * `filter.paramValue` will be the value that is used when serialzing
   * into URL params.
   */
  syncUrlParams?: boolean
  initialValues?: { [key in keyof Filters]?: FilterManagerFilter<Filters[key]> }
}

const getResolver = (
  key,
  { resolve }: UseFilterManagerOptions,
  resolveAlias: ResolveAlias
): FilterManagerResolveFunction<any> => {
  const resolveKey = resolveAlias[key] || key

  return resolve[resolveKey] || FilterManagerResolver[resolveKey]
}

export function useFilterManager<Filters = any>({
  params = [],
  resolve = {},
  formatLabels = {},
  formatTooltips = {},
  lockedTags = {},
  hideTags = {},
  combineTags = {},
  syncUrlParams = true,
  initialValues = {},
}: UseFilterManagerOptions<Filters> = {}): FilterManagerContextValues<Filters> {
  const location = useLocation()
  const { search, modifySearch } = useLocationHelper()

  const options: UseFilterManagerOptions<Filters> = {
    params,
    resolve,
    lockedTags,
    formatLabels,
    formatTooltips,
    hideTags,
    combineTags,
  }

  const addFilter = (filter: FilterManagerFilter) => {
    if (!filter) return

    dispatch({
      type: 'ADD_FILTER',
      filter: {
        [filter.key]: {
          ...filter,
          __resolved: true,
        },
      },
      options,
    })
  }

  const removeFilter = (
    removedFilters: FilterManagerFilter | FilterManagerFilter[]
  ) => {
    if (!removedFilters) return

    const _removedFilters =
      removedFilters instanceof Array ? removedFilters : [removedFilters]

    _removedFilters.forEach((filter) => {
      if (!syncUrlParams) return

      /**
       * Remove the URL params of all combined tags
       */
      if (options.combineTags?.[filter.key]) {
        modifySearch(
          /** Create an object with value of undefined of each combined tag to remove from the url */
          options.combineTags?.[filter.key].reduce((prev, curr) => {
            prev[curr] = undefined

            return prev
          }, {})
        )
      } else {
        search(filter.key, null)
      }
    })

    dispatch({
      type: 'REMOVE_FILTERS',
      filters: _removedFilters,
      options,
    })
  }

  const toggleFilter = (
    filter: FilterManagerFilter,
    value?: string | boolean
  ) => {
    if (!filter) return

    if (!isUndefined(value)) {
      if (value) {
        addFilter(filter)
      } else {
        removeFilter(filter)
      }
    } else {
      if (filters[filter.key] && filters[filter.key].value) {
        addFilter(filter)
      } else {
        removeFilter(filter)
      }
    }
  }

  const clearFilters = () => {
    if (syncUrlParams) {
      modifySearch(mapValues(filters, () => undefined))
    }

    dispatch({
      type: 'CLEAR_FILTERS',
    })
  }

  const resetToInitialValues = () => {
    clearFilters()

    if (!initialValues) return

    Object.values<FilterManagerFilter>(initialValues).forEach((filter) => {
      addFilter(filter)
    })
  }

  const getContextValues = (): FilterManagerContextValues => {
    /**
     * Filter `filterTags` against the `hideTags` options
     */
    const finalFilterTags = filterTags.filter((filterTag) => {
      if (typeof hideTags[filterTag.key] === 'function') {
        return !hideTags[filterTag.key](filterTag.value)
      } else {
        return !hideTags[filterTag.key]
      }
    })

    return {
      filters,
      filterValues: Object.values(filters).reduce((prev, filter) => {
        prev[filter.key] = filter.value
        return prev
      }, {}),
      paramValues: Object.values(filters).reduce((prev, filter) => {
        prev[filter.key] = filter.paramValue
        return prev
      }, {}),
      queryParams,
      filterTags: finalFilterTags,
      addFilter,
      removeFilter,
      toggleFilter,
      clearFilters,
      resetToInitialValues,
      FilterTagsProps: {
        tags: finalFilterTags,
        onClear: clearFilters,
        onRemove: (tags, removedFilter) => {
          removeFilter(removedFilter)
        },
      },
    }
  }

  /**
   * Processes the params option
   */
  const processParams = () => {
    /**
     * This stores a map of any alias resolvers
     */
    const aliasResolvers = {}

    /**
     * Get an array of param keys striped of the alias syntax
     */
    const resolveKeys = params.map((key) => {
      const resolveKey = key.split(':')[0]
      const alias = key.split(':')[1]

      if (alias) {
        aliasResolvers[resolveKey] = alias
      }

      return resolveKey
    })

    return {
      aliasResolvers,
      paramKeys: resolveKeys,
    }
  }

  /**
   * Initialize FilterManager
   */
  const { aliasResolvers: initialAliasResolvers, paramKeys } = processParams()

  /**
   * Take the raw { paramKey: paramValue } from the URL
   * and return an initial state of all the params
   */
  const convertAndResolveUrlParamsToFilters = (
    urlParams: QueryString.ParsedQuery
  ): IFilterManagerFilters => {
    const newParams: IFilterManagerFilters = {}

    Object.keys(urlParams).forEach((key) => {
      let param: FilterManagerFilter = {
        key,
        value: urlParams[key],
        paramValue: urlParams[key],
        __resolved: false,
      }

      const resolverFn = getResolver(key, options, initialAliasResolvers)

      if (typeof resolverFn === 'function') {
        const resolveReturn = resolverFn(
          param.paramValue as string,
          param.key,
          urlParams
        )

        if (resolveReturn?.then) {
          resolveReturn
            .then((resolvedValue) => {
              const newResolvedFilter = {
                key: param.key,
                value: resolvedValue,
                paramValue: param.paramValue,
                __resolved: true,
              }

              dispatch({
                type: 'ADD_FILTER',
                filter: {
                  [param.key]: newResolvedFilter,
                },
                options,
              })
            })
            .catch((e) => console.error(e))
        } else {
          param = {
            ...param,
            value: resolveReturn,
            __resolved: true,
          }
        }
      }

      newParams[key] = param
    })

    return newParams
  }

  const [{ filters, filterTags, queryParams }, dispatch] = useReducer<
    Reducer<FilterManagerState, UseFilterManagerActions>,
    FilterManagerState
  >(
    reducer,
    {
      filters: {},
      filterTags: [],
      queryParams: {},
      resolveAlias: initialAliasResolvers,
      options,
    },
    (initState: FilterManagerState): FilterManagerState => {
      /**
       * Using the processed params array,
       * grab all the param values using those keys from
       * the `location.search`
       */
      const urlParams = pick<{}>(QueryString.parse(location.search), paramKeys)

      const initialFilters = {
        ...Object.keys(initialValues).reduce((prev, key) => {
          prev[key] = {
            ...initialValues[key],
            __resolved: true,
          }
          return prev
        }, {}),
        ...convertAndResolveUrlParamsToFilters(urlParams),
      }

      return produce(initState, (draft) => {
        const newFilterTags = convertFiltersToFilterTags(
          initialFilters,
          initState.options
        )
        const newQueryParams = convertFiltersToQueryParams(initialFilters)

        draft.filters = initialFilters
        draft.filterTags = newFilterTags
        draft.queryParams = newQueryParams
      })
    }
  )

  /**
   * Sync the URL params when queryParams changes
   */
  useEffect(() => {
    if (syncUrlParams) {
      modifySearch(queryParams)
    }
  }, [queryParams])

  return getContextValues()
}
